import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, Grid } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { Device } from "./types";
import { useDevices } from "./DeviceProvider";

const { useBreakpoint } = Grid;
export type DeviceListVariant = "compact" | "full";

interface DeviceListModuleSingleProps {
  onDeviceSelected: (device: Device | null) => void;
  selectedDeviceId?: string | null;
  renderActions?: (record: Device) => React.ReactNode;
  /** compact: デバイス操作など向けの簡易列 / full: デバイス管理用の詳細列 */
  variant?: DeviceListVariant;
}

const { Option } = Select;

const LOCAL_STORAGE_KEY = "deviceListFilterSettings";

interface DeviceFilterValues {
  managerEmail?: string;
  email?: string;
  userDisplayName?: string;
  plan?: string;
  userId?: string;
  expiryFrom?: string | Dayjs | null;
  expiryTo?: string | Dayjs | null;
  serialNumber?: string;
  appVersion?: string;
  osVersion?: string;
}

interface FilterSettings {
  filters: DeviceFilterValues;
  pageSize: number;
}

const includesIgnoreCase = (source: string | null | undefined, query: string) =>
  (source ?? "").toLowerCase().includes(query.trim().toLowerCase());

const hasText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const hasDate = (value: unknown) => {
  if (value === null || value === undefined || value === "") return false;
  return dayjs(value as string | Dayjs).isValid();
};

const hasActiveFilters = (filters?: DeviceFilterValues) => {
  if (!filters) return false;
  return (
    hasText(filters.managerEmail) ||
    hasText(filters.email) ||
    hasText(filters.userDisplayName) ||
    hasText(filters.plan) ||
    hasText(filters.userId) ||
    hasDate(filters.expiryFrom) ||
    hasDate(filters.expiryTo) ||
    hasText(filters.serialNumber) ||
    hasText(filters.appVersion) ||
    hasText(filters.osVersion)
  );
};

const expiryToMs = (value?: number) => {
  if (value == null || !Number.isFinite(value)) return undefined;
  return value < 1e12 ? value * 1000 : value;
};

const matchesExpiryRange = (
  expiry: number | undefined,
  from?: string | Dayjs | null,
  to?: string | Dayjs | null
) => {
  const useFrom = hasDate(from);
  const useTo = hasDate(to);
  if (!useFrom && !useTo) return true;
  const ms = expiryToMs(expiry);
  if (ms == null) return false;
  if (useFrom && ms < dayjs(from as string | Dayjs).startOf("day").valueOf()) return false;
  if (useTo && ms > dayjs(to as string | Dayjs).endOf("day").valueOf()) return false;
  return true;
};

const matchesAndroidVersion = (osVersion: string | undefined, query: string) => {
  const q = query.trim();
  if (!q) return true;
  if (!osVersion) return false;
  if (/^\d{1,2}$/.test(q)) {
    const first = osVersion.match(/\d{1,2}/);
    return first?.[0] === q;
  }
  return includesIgnoreCase(osVersion, q);
};

const serializeFilters = (filters: DeviceFilterValues): DeviceFilterValues => ({
  ...filters,
  expiryFrom: hasDate(filters.expiryFrom)
    ? dayjs(filters.expiryFrom as string | Dayjs).format("YYYY-MM-DD")
    : undefined,
  expiryTo: hasDate(filters.expiryTo)
    ? dayjs(filters.expiryTo as string | Dayjs).format("YYYY-MM-DD")
    : undefined,
});

const restoreFilters = (filters: DeviceFilterValues): DeviceFilterValues => ({
  ...filters,
  expiryFrom: hasDate(filters.expiryFrom) ? dayjs(filters.expiryFrom as string | Dayjs) : undefined,
  expiryTo: hasDate(filters.expiryTo) ? dayjs(filters.expiryTo as string | Dayjs) : undefined,
});

const formatDuration = (ms?: number) => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}時間 ${minutes}分`;
};

const formatTimestamp = (v: number | string | undefined, withTime: boolean) => {
  if (!v) return "—";
  const date = new Date(typeof v === "number" && v < 1e12 ? v * 1000 : v);
  return withTime ? date.toLocaleString("ja-JP") : date.toLocaleDateString("ja-JP");
};

const formatPlan = (v?: string) => {
  if (v === "standard_plan") return "standard_plan";
  if (v === "free") return "free";
  return "—";
};

const formatAppVersion = (r: Device) =>
  r.appVersion ?? (r.versionName ? `${r.versionName} (${r.versionCode ?? "—"})` : "—");

const formatCrash = (r: Device) => {
  const count = r.crashCount ?? 0;
  if (count <= 0) return { text: "0", title: "0" };
  const when = r.lastCrashAt
    ? new Date(r.lastCrashAt < 1e12 ? r.lastCrashAt * 1000 : r.lastCrashAt).toLocaleString("ja-JP")
    : "";
  const text = [String(count) + "回", when].filter((s) => s.length > 0).join(" / ");
  const title = [text, r.lastCrashSummary].filter(Boolean).join("\n");
  return { text, title };
};

const compactColumns: ColumnsType<Device> = [
  {
    title: "管理者メール",
    dataIndex: "managerEmail",
    key: "managerEmail",
    ellipsis: true,
    render: (v, r) => v ?? r.managerEmail ?? "—",
  },
  { title: "デバイス名", dataIndex: "deviceName", key: "deviceName", ellipsis: true },
  {
    title: "ユーザー名",
    dataIndex: "userDisplayName",
    key: "userDisplayName",
    ellipsis: true,
    render: (_, r) => r.userDisplayName ?? r.userInfo?.userName ?? "—",
  },
  {
    title: "プラン",
    dataIndex: "plan",
    key: "plan",
    width: 130,
    render: (v) => formatPlan(v),
  },
  {
    title: "有効期限",
    dataIndex: "expiry",
    key: "expiry",
    width: 120,
    render: (v) => formatTimestamp(v, false),
  },
  {
    title: "appVersion",
    dataIndex: "appVersion",
    key: "appVersion",
    width: 130,
    ellipsis: true,
    render: (_, r) => formatAppVersion(r),
  },
  {
    title: "チャット時間",
    dataIndex: "totalChatTimeMs",
    key: "totalChatTimeMs",
    width: 140,
    render: (v) => formatDuration(v),
  },
  {
    title: "最終同期",
    dataIndex: "lastSyncTime",
    key: "lastSyncTime",
    width: 170,
    render: (v) => formatTimestamp(v, true),
  },
];

const fullColumns: ColumnsType<Device> = [
  {
    title: "管理者メール",
    dataIndex: "managerEmail",
    key: "managerEmail",
    ellipsis: true,
    render: (v, r) => v ?? r.managerEmail ?? "—",
  },
  { title: "デバイス名", dataIndex: "deviceName", key: "deviceName", ellipsis: true },
  {
    title: "ユーザー名",
    dataIndex: "userDisplayName",
    key: "userDisplayName",
    ellipsis: true,
    render: (_, r) => r.userDisplayName ?? r.userInfo?.userName ?? "—",
  },
  {
    title: "プラン",
    dataIndex: "plan",
    key: "plan",
    width: 130,
    render: (v) => formatPlan(v),
  },
  {
    title: "有効期限",
    dataIndex: "expiry",
    key: "expiry",
    width: 120,
    render: (v) => formatTimestamp(v, false),
  },
  {
    title: "チャット時間",
    dataIndex: "totalChatTimeMs",
    key: "totalChatTimeMs",
    width: 140,
    render: (v) => formatDuration(v),
  },
  {
    title: "制限時間",
    key: "chatTimeLimit",
    width: 150,
    render: (_, r) => {
      const warn = typeof r.chat_warn === "number" && Number.isFinite(r.chat_warn) ? r.chat_warn : 0;
      const limit = typeof r.chat_limit === "number" && Number.isFinite(r.chat_limit) ? r.chat_limit : 0;
      return `警告 ${warn} / 上限 ${limit}`;
    },
  },
  {
    title: "アプリ版",
    dataIndex: "appVersion",
    key: "appVersion",
    width: 130,
    ellipsis: true,
    render: (_, r) => formatAppVersion(r),
  },
  {
    title: "クラッシュ",
    dataIndex: "crashCount",
    key: "crashCount",
    width: 160,
    ellipsis: true,
    render: (_, r) => {
      const { text, title } = formatCrash(r);
      return <span title={title}>{text}</span>;
    },
  },
  {
    title: "最終同期",
    dataIndex: "lastSyncTime",
    key: "lastSyncTime",
    width: 170,
    render: (v) => formatTimestamp(v, true),
  },
];

const DeviceListModuleSingle: React.FC<DeviceListModuleSingleProps> = ({
  onDeviceSelected,
  selectedDeviceId,
  renderActions,
  variant = "compact",
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { devices, isLoading, isError } = useDevices();
  const [filteredDeviceData, setFilteredDeviceData] = useState<Device[]>([]);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [filterForm] = Form.useForm<DeviceFilterValues>();
  const [pageSize, setPageSize] = useState<number>(10);
  const [isFiltered, setIsFiltered] = useState(false);
  const isFull = variant === "full";

  const applyFilter = useCallback((allDevices: Device[], filters?: DeviceFilterValues): Device[] => {
    if (!hasActiveFilters(filters)) {
      return allDevices;
    }
    const f = filters ?? {};
    return allDevices.filter((device) => {
      if (hasText(f.managerEmail) && !includesIgnoreCase(device.managerEmail, f.managerEmail)) {
        return false;
      }
      if (hasText(f.email)) {
        const emailQuery = f.email;
        const emails = [device.managerEmail, ...(device.familyEmails ?? [])];
        if (!emails.some((email) => includesIgnoreCase(email, emailQuery))) return false;
      }
      if (hasText(f.userDisplayName)) {
        const nameQuery = f.userDisplayName;
        const names = [device.userDisplayName, device.userInfo?.userName];
        if (!names.some((name) => includesIgnoreCase(name, nameQuery))) return false;
      }
      if (hasText(f.plan) && device.plan !== f.plan) {
        return false;
      }
      if (hasText(f.userId)) {
        const uidQuery = f.userId;
        const uids = [device.userId, device.userInfo?.userID];
        if (!uids.some((uid) => includesIgnoreCase(uid, uidQuery))) return false;
      }
      if (!matchesExpiryRange(device.expiry, f.expiryFrom, f.expiryTo)) {
        return false;
      }
      if (hasText(f.serialNumber)) {
        const idQuery = f.serialNumber;
        const ids = [device.serialNumber, device.id];
        if (!ids.some((id) => includesIgnoreCase(id, idQuery))) return false;
      }
      if (hasText(f.appVersion)) {
        const versionQuery = f.appVersion;
        const versions = [
          device.appVersion,
          device.versionName,
          device.versionCode != null ? String(device.versionCode) : undefined,
        ];
        if (!versions.some((ver) => includesIgnoreCase(ver, versionQuery))) return false;
      }
      if (hasText(f.osVersion) && !matchesAndroidVersion(device.osVersion, f.osVersion)) {
        return false;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    if (devices) {
      try {
        const savedSettings = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedSettings) {
          const { filters, pageSize: savedPageSize } = JSON.parse(savedSettings) as FilterSettings;
          const restored = restoreFilters(filters || {});
          filterForm.setFieldsValue(restored);
          setPageSize(savedPageSize || 10);
          setFilteredDeviceData(applyFilter(devices, restored));
          setIsFiltered(hasActiveFilters(restored));
        } else {
          setFilteredDeviceData(devices);
          setIsFiltered(false);
        }
      } catch (error) {
        console.error("Failed to parse filter settings from localStorage", error);
        setFilteredDeviceData(devices);
      }
    }
  }, [devices, applyFilter, filterForm]);

  const saveSettings = (filters: DeviceFilterValues, size: number) => {
    try {
      const settings: FilterSettings = { filters: serializeFilters(filters), pageSize: size };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error("Failed to save filter settings to localStorage", error);
    }
  };

  const handleFilter = (values: DeviceFilterValues) => {
    if (!devices) return;
    setFilteredDeviceData(applyFilter(devices, values));
    setIsFilterModalVisible(false);
    setIsFiltered(hasActiveFilters(values));
    saveSettings(values, pageSize);
  };

  const handleClearFilter = () => {
    filterForm.resetFields();
    setFilteredDeviceData(devices || []);
    setIsFiltered(false);
    setPageSize(10);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    onDeviceSelected(null);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    saveSettings(filterForm.getFieldsValue(), value);
  };

  const columns: ColumnsType<Device> = [...(isFull ? fullColumns : compactColumns)];

  if (renderActions) {
    columns.push({
      title: "操作",
      key: "actions",
      // モバイルでは固定列を外し幅を抑える（縦画面で一覧が潰れるのを防ぐ）
      width: isMobile ? 72 : 150,
      fixed: isMobile ? undefined : "right",
      render: (_, record) => renderActions(record),
    });
  }

  const filterButtonStyle = isFiltered
    ? { backgroundColor: "#faad14", borderColor: "#faad14", color: "white" }
    : {};

  if (isError) return <div>データ取得エラー</div>;

  return (
    <div style={{ marginBottom: 16, minWidth: 0, width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: 8,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Button
          type="primary"
          onClick={() => setIsFilterModalVisible(true)}
          style={{ marginRight: 8, ...filterButtonStyle }}
          icon={<FilterOutlined />}
        >
          フィルタ
        </Button>
        <Button onClick={handleClearFilter} style={{ marginRight: 16 }}>
          フィルタ解除
        </Button>
        <div>
          <span style={{ marginRight: 8 }}>最大件数:</span>
          <Select value={pageSize} onChange={handlePageSizeChange} style={{ width: 120 }}>
            <Option value={10}>10件</Option>
            <Option value={50}>50件</Option>
            <Option value={100}>100件</Option>
          </Select>
        </div>
      </div>

      <Table
        className="device-table"
        dataSource={filteredDeviceData}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize }}
        onRow={(record) => ({ onClick: () => onDeviceSelected(record) })}
        rowClassName={(record) => `row-hover ${selectedDeviceId === record.id ? "selected-row" : ""}`}
        scroll={{ x: "max-content" }}
        style={{ width: "100%" }}
      />

      <Modal
        title="フィルタ"
        open={isFilterModalVisible}
        onCancel={() => setIsFilterModalVisible(false)}
        onOk={() => filterForm.submit()}
        okText="適用"
        cancelText="キャンセル"
        width={isMobile ? undefined : 520}
        style={isMobile ? { top: 16 } : undefined}
      >
        <Form form={filterForm} onFinish={handleFilter} layout="vertical">
          <Form.Item name="managerEmail" label="管理者メール">
            <Input allowClear placeholder="管理者メールで検索" />
          </Form.Item>
          <Form.Item
            name="email"
            label="メールアドレス"
            extra="管理者メールと親族メールの両方から検索します"
          >
            <Input allowClear placeholder="メールアドレスで検索" />
          </Form.Item>
          <Form.Item name="userDisplayName" label="ユーザー名">
            <Input allowClear placeholder="ユーザー名で検索" />
          </Form.Item>
          <Form.Item name="plan" label="プラン">
            <Select
              placeholder="すべて"
              allowClear
              options={[
                { value: "free", label: "free（お試し）" },
                { value: "standard_plan", label: "standard_plan（サブスク）" },
              ]}
            />
          </Form.Item>
          <Form.Item name="userId" label="ユーザーUID">
            <Input allowClear placeholder="Firebase Auth UID で検索" />
          </Form.Item>
          <Form.Item label="有効期限">
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item name="expiryFrom" noStyle>
                <DatePicker style={{ width: "50%" }} placeholder="From" format="YYYY-MM-DD" allowClear />
              </Form.Item>
              <Form.Item name="expiryTo" noStyle>
                <DatePicker style={{ width: "50%" }} placeholder="To" format="YYYY-MM-DD" allowClear />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="serialNumber" label="デバイスID">
            <Input allowClear placeholder="デバイスIDで検索" />
          </Form.Item>
          <Form.Item name="appVersion" label="アプリ版">
            <Input allowClear placeholder="アプリ版で検索" />
          </Form.Item>
          <Form.Item
            name="osVersion"
            label="Androidバージョン"
            extra="13、14、15 などの数字で指定できます"
          >
            <Input allowClear placeholder="例: 14" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceListModuleSingle;
